import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Pages load on demand, each from its own file, so opening Flow downloads the
 * app frame and the one page being opened rather than every page there is.
 *
 * `lazyPage` is React.lazy plus the two things React.lazy does not do:
 *
 * 1. PRELOAD. `Page.preload()` starts the download without rendering anything:
 *    for the page the browser opened at (so its file arrives while sign-in is
 *    still being checked, not after) and for a sidebar link under the pointer.
 *    Preload and render share one request.
 *
 * 2. RECOVER FROM A DEPLOY. Every build names its files afresh, and Vercel
 *    serves only the current build's. A tab opened before a deploy still holds
 *    the old names, so the next page it opens asks for a file that is gone. The
 *    cure is the new build: reload once, and the browser fetches the current
 *    index.html and, with it, the current names. The navigation has already
 *    changed the address, so the reload lands on the page that was asked for.
 *    A guard stops this becoming a loop when the failure is something a reload
 *    cannot fix; then the page's error boundary says so.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- any props: this wraps every page
type AnyComponent = ComponentType<any>;

export type LazyPage<T extends AnyComponent> = LazyExoticComponent<T> & {
  /** Start downloading the page's code now. Never throws, never rejects. */
  preload: () => void;
};

/** A page from a module's default export. */
export function lazyPage<T extends AnyComponent>(load: () => Promise<{ default: T }>): LazyPage<T>;
/** A page from a named export — how most of Flow's pages are written. */
export function lazyPage<M, K extends keyof M>(
  load: () => Promise<M>,
  exportName: K
): LazyPage<Extract<M[K], AnyComponent>>;
export function lazyPage(
  load: () => Promise<Record<string, unknown>>,
  exportName: string = 'default'
): LazyPage<AnyComponent> {
  let request: Promise<{ default: AnyComponent }> | null = null;

  const fetchPage = () => {
    if (!request) {
      const attempt = load().then((module) => ({ default: module[exportName] as AnyComponent }));
      // A failed request is not kept, so a preload that failed does not decide
      // the render: the render makes its own attempt, and recovers if that fails.
      attempt.catch(() => {
        if (request === attempt) request = null;
      });
      request = attempt;
    }
    return request;
  };

  const Page = lazy(() => fetchPage().catch(recoverFromFailedLoad)) as LazyPage<AnyComponent>;
  Page.preload = () => {
    fetchPage().catch(() => undefined);
  };
  return Page;
}

/** What the error boundary shows once reloading has been tried, or cannot help. */
export class PageLoadError extends Error {
  constructor(cause: unknown) {
    super(
      "This page couldn't be loaded. Check your connection and reload — if Flow was " +
        'just updated, reloading fetches the new version.'
    );
    this.name = 'PageLoadError';
    (this as { cause?: unknown }).cause = cause;
  }
}

function recoverFromFailedLoad(error: unknown): Promise<never> {
  if (reloadForCurrentBuild()) {
    console.warn('[lazyPage] a page failed to load; reloading once for the current build', error);
    // Hold the loading state until the browser replaces the document.
    return new Promise<never>(() => {});
  }
  console.error('[lazyPage] a page failed to load', error);
  return Promise.reject(new PageLoadError(error));
}

const RELOAD_KEY = 'flow:page-load-reload';

/** Never reload twice for the same address within this long. */
export const SAME_PAGE_WINDOW_MS = 5 * 60_000;
/** Never reload twice at all within this long, whatever the address. */
export const ANY_PAGE_WINDOW_MS = 10_000;

export interface ReloadMark {
  path: string;
  at: number;
}

/**
 * Whether a failed page load may reload the document, given the last reload
 * this tab made for that reason. Pure, so the loop guard can be tested.
 *
 * A second failure on the same address soon after a reload means the reload
 * did not help — a reload cannot fix an outage or a bug, and repeating it would
 * loop. Any two reloads in quick succession are refused for the same reason.
 * A clock that has gone backwards reads as "just now": when in doubt, do not.
 */
export function decideReload(previous: ReloadMark | null, path: string, now: number): boolean {
  if (!previous) return true;
  const age = now - previous.at;
  if (age < ANY_PAGE_WINDOW_MS) return false;
  if (previous.path === path && age < SAME_PAGE_WINDOW_MS) return false;
  return true;
}

function parseMark(raw: string | null): ReloadMark | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      value &&
      typeof value === 'object' &&
      typeof (value as ReloadMark).path === 'string' &&
      typeof (value as ReloadMark).at === 'number'
    ) {
      return value as ReloadMark;
    }
  } catch {
    /* a malformed mark is no mark */
  }
  return null;
}

function reloadForCurrentBuild(): boolean {
  if (typeof window === 'undefined') return false;
  // Offline, a reload only swaps a page with a message for the browser's own
  // error page.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;

  const path = window.location.pathname;
  let previous: ReloadMark | null;
  try {
    previous = parseMark(window.sessionStorage.getItem(RELOAD_KEY));
  } catch {
    // No storage, no loop guard: better an error message than a reload loop.
    return false;
  }
  const now = Date.now();
  if (!decideReload(previous, path, now)) return false;

  try {
    window.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ path, at: now } satisfies ReloadMark));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
